/**
 * Component: Info Tooltip
 * -----------------------------------------------------------------------------
 * - A lightweight tooltip component that displays on click (default) or hover.
 * - Supports keyboard accessibility (Escape to close) and ensures only one
 *   tooltip is visible at a time.
 * - Auto-positions based on viewport bounds with smart flip and constrain logic.
 *
 * ## Positioning strategy:
 *  - Default placement: to the right of the trigger, aligned above.
 *  - If overflowing right  → flip to the left side of the trigger.
 *  - If flipped-left still overflows left → constrain (pin) to viewport left.
 *  - If overflowing top    → flip below the trigger.
 *  - If overflowing bottom → flip above the trigger.
 *
 * ## Usage Example:
 * <info-tooltip
 *   class="info-tooltip"
 *   data-hover
 * >
 *   <button
 *     type="button"
 *     class="info-tooltip__button"
 *     data-js-tooltip-button
 *     aria-describedby="tooltip-sample"
 *   >
 *     {% render 'icon-info' %}
 *   </button>
 *   <span
 *     id="tooltip-sample"
 *     role="tooltip"
 *     class="info-tooltip__content"
 *     data-js-tooltip-content
 *   >
 *     Tooltip content goes here.
 *   </span>
 * </info-tooltip>
 */

;(() => {
  if (customElements.get('info-tooltip')) {
    return
  }

  class InfoTooltip extends HTMLElement {
    /**
     * STATIC PROPERTIES & CONFIGURATION
     * -------------------------------------------------------------------------
     */

    /** Minimum distance (px) between the tooltip edge and the viewport edge */
    static VIEWPORT_MARGIN = 8

    static _attrs = {
      hover: 'data-hover',
    }

    static _classes = {
      visible: 'is-visible',
      flipLeft: 'is-flipped-left',
      flipTop: 'is-flipped-top',
      flipBottom: 'is-flipped-bottom',
      constrainedLeft: 'is-constrained-left',
    }

    static _selectors = {
      button: '[data-js-tooltip-button]',
      content: '[data-js-tooltip-content]',
    }

    static _cssVars = {
      constrainLeft: '--tooltip-constrain-left',
    }

    /**
     * CONSTRUCTOR & LIFECYCLE
     * -------------------------------------------------------------------------
     */
    constructor() {
      super()

      /** @type {HTMLElement|null} */
      this.button = null

      /** @type {HTMLElement|null} */
      this.tooltip = null

      // Bind event handlers to preserve context
      this.onPointerDown = this.pointerDownHandler.bind(this)
      this.onOutsideClick = this.outsideClickHandler.bind(this)
      this.onEscape = this.escapeHandler.bind(this)
      this.onMouseEnter = this.mouseEnterHandler.bind(this)
      this.onMouseLeave = this.mouseLeaveHandler.bind(this)
    }

    /**
     * Lifecycle: Called when element is connected to DOM
     */
    connectedCallback() {
      this.initialize()
    }

    /**
     * Lifecycle: Called when element is removed from DOM
     */
    disconnectedCallback() {
      this.cleanup()
    }

    /**
     * INITIALIZATION & CLEANUP
     * -------------------------------------------------------------------------
     */

    /**
     * Initialize the tooltip component
     */
    initialize() {
      this.setElements()
      this.addEventListeners()
    }

    /**
     * Cleanup when component is removed
     */
    cleanup() {
      this.removeEventListeners()
    }

    /**
     * Query and store references to button and tooltip elements
     */
    setElements() {
      this.button = this.querySelector(InfoTooltip._selectors.button)
      this.tooltip = this.querySelector(InfoTooltip._selectors.content)
    }

    /**
     * EVENT HANDLING
     * -------------------------------------------------------------------------
     */

    /**
     * Add all necessary event listeners
     */
    addEventListeners() {
      if (!this.button || !this.tooltip) {
        return
      }

      const prefersHover = this.hasAttribute(InfoTooltip._attrs.hover)

      this.button.addEventListener('pointerdown', this.onPointerDown)
      document.addEventListener('pointerdown', this.onOutsideClick)
      document.addEventListener('keydown', this.onEscape)

      if (prefersHover) {
        this.button.addEventListener('mouseenter', this.onMouseEnter)
        this.button.addEventListener('mouseleave', this.onMouseLeave)
      }
    }

    /**
     * Remove all event listeners.
     * Calling removeEventListener for a never-added listener is a safe no-op,
     * so we unconditionally remove hover listeners without checking the attribute.
     */
    removeEventListeners() {
      if (!this.button) {
        return
      }

      this.button.removeEventListener('pointerdown', this.onPointerDown)
      document.removeEventListener('pointerdown', this.onOutsideClick)
      document.removeEventListener('keydown', this.onEscape)
      this.button.removeEventListener('mouseenter', this.onMouseEnter)
      this.button.removeEventListener('mouseleave', this.onMouseLeave)
    }

    /**
     * Show the tooltip on mouse enter (hover mode)
     */
    mouseEnterHandler() {
      this.showTooltip()
    }

    /**
     * Hide the tooltip on mouse leave (hover mode)
     */
    mouseLeaveHandler() {
      this.hideTooltip()
    }

    /**
     * Toggle tooltip visibility on pointer down
     * @param {PointerEvent} event - Pointer event triggered by interaction
     */
    pointerDownHandler(event) {
      event.preventDefault()
      this.toggleTooltip()
    }

    /**
     * Hide tooltip when clicking outside the component
     * @param {Event} event - Click or pointer event
     */
    outsideClickHandler(event) {
      if (event.target instanceof Node && !this.contains(event.target)) {
        this.hideTooltip()
      }
    }

    /**
     * Hide tooltip when pressing the Escape key
     * @param {KeyboardEvent} event - Key press event
     */
    escapeHandler(event) {
      if (event.key === 'Escape') {
        this.hideTooltip()
      }
    }

    /**
     * CORE FUNCTIONALITY
     * -------------------------------------------------------------------------
     */

    /**
     * Show the tooltip and hide all other tooltips
     */
    showTooltip() {
      InfoTooltip.hideAllTooltips()

      this.updatePosition()
      this.tooltip?.classList.add(InfoTooltip._classes.visible)
    }

    /**
     * Hide the tooltip
     */
    hideTooltip() {
      this.tooltip?.classList.remove(InfoTooltip._classes.visible)
    }

    /**
     * Toggle the tooltip visibility
     */
    toggleTooltip() {
      if (!this.tooltip) {
        return
      }

      const isVisible = this.tooltip.classList.contains(
        InfoTooltip._classes.visible
      )

      if (isVisible) {
        this.hideTooltip()
      } else {
        this.showTooltip()
      }
    }

    /**
     * POSITIONING
     * -------------------------------------------------------------------------
     */

    /**
     * Recalculate and apply the optimal tooltip position.
     * Resets previous positioning, then applies horizontal and vertical flips
     * based on the tooltip's relation to the viewport edges.
     */
    updatePosition() {
      if (!this.tooltip) {
        return
      }

      const el = this.tooltip

      this.resetPositionState(el)

      // Force a reflow so measurements reflect current DOM state
      void el.offsetHeight

      const rect = el.getBoundingClientRect()

      this.applyHorizontalFlip(el, rect)
      this.applyVerticalFlip(el, rect)
    }

    /**
     * Clear all positioning classes and CSS custom properties
     *
     * @param {HTMLElement} el - The tooltip element
     */
    resetPositionState(el) {
      const { flipLeft, flipTop, flipBottom, constrainedLeft } =
        InfoTooltip._classes

      el.classList.remove(flipLeft, flipTop, flipBottom, constrainedLeft)
      el.style.removeProperty(InfoTooltip._cssVars.constrainLeft)
    }

    /**
     * Flip tooltip to the left side of the trigger when it overflows the
     * right viewport edge. If the flipped position also overflows left,
     * delegate to constrainToViewport() for a pinned fallback.
     *
     * @param {HTMLElement} el - The tooltip element
     * @param {DOMRect} rect - Bounding rect of the tooltip in default position
     */
    applyHorizontalFlip(el, rect) {
      const overflowsRight =
        rect.right > window.innerWidth - InfoTooltip.VIEWPORT_MARGIN

      if (!overflowsRight) {
        return
      }

      el.classList.add(InfoTooltip._classes.flipLeft)
      this.constrainToViewport(el)
    }

    /**
     * Pin the tooltip to the viewport left edge when the flipped-left
     * position would still overflow. Computes the required offset relative
     * to the host element and applies it via a CSS custom property.
     *
     * @param {HTMLElement} el - The tooltip element
     */
    constrainToViewport(el) {
      // Re-measure after the flip class was applied
      void el.offsetHeight

      const rect = el.getBoundingClientRect()
      const margin = InfoTooltip.VIEWPORT_MARGIN

      if (rect.left >= margin) {
        return
      }

      const hostRect = this.getBoundingClientRect()
      const offset = margin - hostRect.left

      el.style.setProperty(InfoTooltip._cssVars.constrainLeft, `${offset}px`)
      el.classList.add(InfoTooltip._classes.constrainedLeft)
    }

    /**
     * Flip tooltip vertically when it overflows the top or bottom
     * viewport edge. Top overflow takes priority (flips below trigger);
     * bottom overflow flips above trigger.
     *
     * @param {HTMLElement} el - The tooltip element
     * @param {DOMRect} rect - Bounding rect of the tooltip in default position
     */
    applyVerticalFlip(el, rect) {
      const margin = InfoTooltip.VIEWPORT_MARGIN

      if (rect.top < margin) {
        el.classList.add(InfoTooltip._classes.flipBottom)
      } else if (rect.bottom > window.innerHeight - margin) {
        el.classList.add(InfoTooltip._classes.flipTop)
      }
    }

    /**
     * STATIC METHODS
     * -------------------------------------------------------------------------
     */

    /**
     * Hide all tooltips in the document
     */
    static hideAllTooltips() {
      document.querySelectorAll('info-tooltip').forEach((el) => {
        if (el instanceof InfoTooltip && el.tooltip) {
          el.tooltip.classList.remove(InfoTooltip._classes.visible)
        }
      })
    }
  }

  customElements.define('info-tooltip', InfoTooltip)
})()
